import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Directive to enforce number limits on input elements.
 *
 * @example
 * <input type="number" appNumberLimits [min]="1" [max]="500">
 */
@Directive({
    selector: '[appNumberInputLimits]',
    standalone: false
})
export class NumberInputLimitDirective {
  @Input() min?: number;
  @Input() max?: number;

  private readonly spanElement: HTMLSpanElement | null = null;
  private hideTimeout: any;
  private originalValue: number | null = null;

  constructor(
    private el: ElementRef,
    private control: NgControl,
    private renderer: Renderer2
  ) {
    this.spanElement = this.renderer.createElement('span');
    this.renderer.addClass(this.spanElement, 'min-max-number-message');

    const parent = this.el.nativeElement.parentNode;
    this.renderer.insertBefore(parent, this.spanElement, this.el.nativeElement.nextSibling);
  }

  private showFeedback(message: string) {
    if (this.spanElement) {
      if (this.hideTimeout)
        clearTimeout(this.hideTimeout);

      this.renderer.setProperty(this.spanElement, 'textContent', message);
      this.renderer.addClass(this.spanElement, 'show');

      this.hideTimeout = setTimeout(() => {
        if (this.spanElement)
          this.renderer.removeClass(this.spanElement, 'show');
      }, 2000);
    }
  }

  @HostListener('focus')
  onFocus() {
    this.originalValue = this.el.nativeElement.value;
  }

  @HostListener('mousedown')
  onMouseDown(): void {
    this.originalValue = this.el.nativeElement.value;
  }

  @HostListener('blur')
  onBlur() {
    const value = parseInt(this.el.nativeElement.value, 10);

    if (isNaN(value)) {
      this.updateFormControl(this.originalValue ?? null);
      this.el.nativeElement.value = this.originalValue ?? '';
    } else if (this.max && value > this.max) {
      this.el.nativeElement.value = this.max;
      this.updateFormControl(this.max);
      this.showFeedback(`Max ${this.max}`);
    } else if (this.min && value < this.min) {
      this.el.nativeElement.value = this.min;
      this.updateFormControl(this.min);
      this.showFeedback(`Min ${this.min}`);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedValue = event.clipboardData?.getData('text');
    let value = parseInt(pastedValue || '', 10);

    if (this.max && value > this.max) {
      value = this.max;
      this.showFeedback(`Max ${this.max}`);
    } else if (this.min && value < this.min) {
      value = this.min;
      this.showFeedback(`Min ${this.min}`);
    }

    this.el.nativeElement.value = value;
    this.updateFormControl(value);
  }

  private updateFormControl(value: number | null) {
    if (this.control && this.control.control) {
      this.control.control.setValue(value, { emitEvent: true });
    }
  }
}
